import { useEffect, useRef } from 'react';

export type KeyHandler = () => void;
export type KeyHandlerMap = Record<string, KeyHandler>;

export interface KeyboardShortcutsOptions {
  enabled?: boolean;
  /** When true, key presses inside form fields are ignored. Defaults to true. */
  ignoreInForms?: boolean;
}

function isFormFieldTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(
  handlers: KeyHandlerMap,
  { enabled = true, ignoreInForms = true }: KeyboardShortcutsOptions = {},
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (ignoreInForms && isFormFieldTarget(event.target)) return;
      const handler =
        handlersRef.current[event.key] ?? handlersRef.current[event.key.toLowerCase()];
      if (handler) {
        event.preventDefault();
        handler();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, ignoreInForms]);
}
